/**
 * Errors thrown by the Lucid Resourceful Library
 * @module @nhtio/lucid-resourceful/errors
 */

/* eslint-disable @typescript-eslint/naming-convention */
import { format } from 'node:util'
import { formatSyntaxError } from '@/private/utils'
import { createError, Exception } from '@poppinss/utils'
import type { SyntaxError } from 'liqe'
import type { ValidationError } from '@nhtio/lucid-resourceful/joi'

// @ts-ignore
interface ErrorOptions {
  cause?: unknown
}

const messageFromJoiValidationError = (reason: ValidationError | undefined, fallback: string) => {
  return reason ? reason.details.map((d) => d.message).join(' and ') : fallback
}

export class E_INVALID_RESOURCEFUL_DATA_TYPE_OPTIONS extends Exception {
  static status = 500
  static code = 'E_INVALID_RESOURCEFUL_DATA_TYPE_OPTIONS'

  declare readonly details?: ValidationError['details']

  constructor(name: string, reason?: ValidationError) {
    const msg: string =
      `Invalid Options for "${name}": ` +
      messageFromJoiValidationError(
        reason,
        'The options provided are not compatible with the data type'
      )
    super(msg, {
      code: 'E_INVALID_RESOURCEFUL_DATA_TYPE_OPTIONS',
      status: 500,
      cause: reason,
    })
    Object.defineProperty(this, 'details', {
      value: reason?.details,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_INVALID_RESOURCEFUL_MIXIN_OPTIONS extends Exception {
  static status = 500
  static code = 'E_INVALID_RESOURCEFUL_MIXIN_OPTIONS'

  declare readonly details?: ValidationError['details']

  constructor(name: string, reason?: ValidationError) {
    const msg: string =
      `Invalid 'withResourceful' options for "${name}": ` +
      messageFromJoiValidationError(
        reason,
        'The options provided are not compatible with the withResourceful mixin'
      )
    super(msg, {
      code: 'E_INVALID_RESOURCEFUL_MIXIN_OPTIONS',
      status: 500,
      cause: reason,
    })
    Object.defineProperty(this, 'details', {
      value: reason?.details,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export const E_INVALID_PREPARED_VALUE = createError<[string, string]>(
  'The value for "%s" is not a valid %s',
  'E_INVALID_PREPARED_VALUE',
  422
)

export const E_INVALID_CONSUMED_VALUE = createError<[string, any, string]>(
  'The value for "%s" (%O) could not be consumed as a valid %s',
  'E_INVALID_CONSUMED_VALUE',
  500
)

export const E_UNCASTABLE = createError<[string]>(
  'The value could not be cast to a valid %s',
  'E_UNCASTABLE',
  500
)

export class E_INVALID_RESOURCEFUL_DECORATOR_OPTIONS extends Exception {
  static status = 500
  static code = 'E_INVALID_RESOURCEFUL_DECORATOR_OPTIONS'

  declare readonly details?: ValidationError['details']

  constructor(decoratorName: string, fieldName: string, reason?: ValidationError) {
    const msg: string =
      `Invalid "@${decoratorName}" Options for "${fieldName}": ` +
      messageFromJoiValidationError(
        reason,
        "The options provided are not compatible with the decorator's requirements"
      )
    super(msg, {
      code: 'E_INVALID_RESOURCEFUL_DECORATOR_OPTIONS',
      status: 500,
      cause: reason,
    })
    Object.defineProperty(this, 'details', {
      value: reason?.details,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_FORBIDDEN extends Exception {
  static status = 403
  static code = 'E_FORBIDDEN'
  constructor(message: string) {
    const msg: string = message
    super(msg, {
      code: 'E_FORBIDDEN',
      status: 403,
    })
    Object.defineProperty(this, 'details', {
      value: msg,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_INVALID_LUCENE_QUERY extends Exception {
  static status = 400
  static code = 'E_INVALID_LUCENE_QUERY'

  constructor(message: string) {
    const msg: string = `Invalid Lucene query: ${message}`
    super(msg, {
      code: 'E_INVALID_LUCENE_QUERY',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: msg,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_INVALID_COLUMN_ACCESS extends Exception {
  static status = 403
  static code = 'E_INVALID_COLUMN_ACCESS'

  constructor(columnName: string) {
    const msg: string = `You don't have access to column "${columnName}" or the column does not exist.`
    super(msg, {
      code: 'E_INVALID_COLUMN_ACCESS',
      status: 403,
    })
    Object.defineProperty(this, 'details', {
      value: msg,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_UNEXPECTED_COLUMN_IN_QUERY extends Exception {
  static status = 400
  static code = 'E_UNEXPECTED_COLUMN_IN_QUERY'

  constructor(columnName: string) {
    const msg: string = `Unexpected column in query: "${columnName}"`
    super(msg, {
      code: 'E_UNEXPECTED_COLUMN_IN_QUERY',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: msg,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_LUCENE_SYNTAX_EXCEPTION extends Exception {
  static status = 422
  static code = 'E_LUCENE_SYNTAX_EXCEPTION'
  declare readonly offset: number
  declare readonly line: number
  declare readonly column: number
  declare readonly details: string

  constructor(query: string, original: SyntaxError) {
    const msg: string = `${original.message} at line ${original.line}, column ${original.column}, offset ${original.offset}`
    super(msg, {
      code: 'E_LUCENE_SYNTAX_EXCEPTION',
      status: 422,
      cause: original,
    })
    Object.defineProperty(this, 'offset', {
      value: original.offset,
      enumerable: true,
      writable: false,
      configurable: false,
    })
    Object.defineProperty(this, 'line', {
      value: original.line,
      enumerable: true,
      writable: false,
      configurable: false,
    })
    Object.defineProperty(this, 'column', {
      value: original.column,
      enumerable: true,
      writable: false,
      configurable: false,
    })
    Object.defineProperty(this, 'details', {
      value: formatSyntaxError(query, original.line, original.column, original.offset),
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_LUCENE_UNEXPECTED_EXCEPTION extends Exception {
  static status = 503
  static code = 'E_LUCENE_UNEXPECTED_EXCEPTION'

  constructor(original: unknown) {
    const msg: string = `Unexpected error while processing Lucene query: ${original instanceof Error ? original.message : String(original)}`
    super(msg, {
      code: 'E_LUCENE_UNEXPECTED_EXCEPTION',
      status: 503,
      cause: original,
    })
  }
}

export class E_LUCENE_INVALID_TYPE extends Exception {
  static status = 503
  static code = 'E_LUCENE_INVALID_TYPE'

  constructor(type: string) {
    const msg: string = `"${type}" is not a valid Lucene AST type.`
    super(msg, {
      code: 'E_LUCENE_INVALID_TYPE',
      status: 503,
    })
  }
}

export class E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION extends Exception {
  static status = 422
  static code = 'E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION'

  declare readonly details?: ValidationError['details']

  constructor(reason?: ValidationError) {
    super(messageFromJoiValidationError(reason, 'Your request could not be fulfilled'), {
      code: 'E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION',
      status: 422,
      cause: reason,
    })
    Object.defineProperty(this, 'details', {
      value: reason?.details,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_MISSING_PRIMARY_KEY_EXCEPTION extends Exception {
  static status = 503
  static code = 'E_MISSING_PRIMARY_KEY_EXCEPTION'

  constructor(type: string) {
    const msg: string = `"${type}" does not have a primary key defined`
    super(msg, {
      code: 'E_MISSING_PRIMARY_KEY_EXCEPTION',
      status: 503,
    })
  }
}

export class E_RECORD_NOT_FOUND_EXCEPTION extends Exception {
  static status = 404
  static code = 'E_RECORD_NOT_FOUND_EXCEPTION'

  constructor() {
    const msg: string = `The record is not present or requester doesn't have access to it.`
    super(msg, {
      code: 'E_RECORD_NOT_FOUND_EXCEPTION',
      status: 404,
    })
  }
}

export class E_RELATIONSHIP_NOT_FOUND_EXCEPTION extends Exception {
  static status = 404
  static code = 'E_RELATIONSHIP_NOT_FOUND_EXCEPTION'

  constructor() {
    const msg: string = `The relationship is not present or requester doesn't have access to it.`
    super(msg, {
      code: 'E_RELATIONSHIP_NOT_FOUND_EXCEPTION',
      status: 404,
    })
  }
}

export class E_UNSYNCABLE_RELATIONSHIP_EXCEPTION extends Exception {
  static status = 400
  static code = 'E_UNSYNCABLE_RELATIONSHIP_EXCEPTION'

  constructor() {
    const msg: string = `The inferred relationships are not syncable.`
    super(msg, {
      code: 'E_UNSYNCABLE_RELATIONSHIP_EXCEPTION',
      status: 400,
    })
  }
}

export class E_INVALID_PAYLOAD_EXCEPTION extends Exception {
  static status = 422
  static code = 'E_INVALID_PAYLOAD_EXCEPTION'

  declare readonly details?: ValidationError['details']

  constructor(reason?: ValidationError) {
    super(
      messageFromJoiValidationError(reason, 'Your submission does not pass validation criteria'),
      {
        code: 'E_INVALID_PAYLOAD_EXCEPTION',
        status: 422,
        cause: reason,
      }
    )
    Object.defineProperty(this, 'details', {
      value: reason?.details,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_FORBIDDEN_PAYLOAD_EXCEPTION extends Exception {
  static status = 403
  static code = 'E_FORBIDDEN_PAYLOAD_EXCEPTION'

  declare readonly details?: ValidationError['details']

  constructor(reason?: ValidationError) {
    super(
      messageFromJoiValidationError(reason, 'Your submission does not pass authorization criteria'),
      {
        code: 'E_FORBIDDEN_PAYLOAD_EXCEPTION',
        status: 403,
        cause: reason,
      }
    )
    Object.defineProperty(this, 'details', {
      value: reason?.details,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_INVALID_RELATIONSHIP_EXCEPTION extends Exception {
  static status = 400
  static code = 'E_INVALID_RELATIONSHIP_EXCEPTION'

  constructor(message: string) {
    super(message, {
      code: 'E_INVALID_RELATIONSHIP_EXCEPTION',
      status: 400,
    })
  }
}

/**
 * Thrown when a cached `belongsTo` relation's `localKey` differs from the related
 * model's `primaryKey`. The relation cache keys a `belongsTo` lookup by the parent
 * FK value (the related row's `localKey`) but tags the stored entry by the related
 * row's `primaryKey`; when these diverge the lookup key and eviction tag describe
 * different rows, so a cached entry could serve the wrong row. The cache fails loud
 * here rather than risk returning incorrect data.
 */
export class E_RELATION_CACHE_KEY_DIVERGENCE extends Exception {
  static status = 500
  static code = 'E_RELATION_CACHE_KEY_DIVERGENCE'

  constructor(
    relationName: string,
    relatedModelName: string,
    localKey: string,
    primaryKey: string
  ) {
    super(
      `Cannot cache belongsTo relation "${relationName}": related model 
"${relatedModelName}" has localKey "${localKey}" which differs from its 
primaryKey "${primaryKey}". The relation cache keys belongsTo lookups by 
localKey but tags entries by primaryKey; a divergence could serve the wrong 
row. Either set localKey to the primary key or remove this relation from 
relationsCache.`,
      {
        code: 'E_RELATION_CACHE_KEY_DIVERGENCE',
        status: 500,
      }
    )
  }
}

/**
 * Structured context attached to every runtime relation-cache operational
 * failure (ADR-009). Identifies the model whose error handler is consulted, plus
 * the relation / lookup key / eviction tag the failing operation concerned.
 *
 * @public
 */
export interface RelationCacheErrorContext {
  /** Canonical identity name of the model involved (the declaring model on reads/writes, the target on evictions). */
  model: string
  /** The relation name, when the failure is tied to a specific relation load. */
  relation?: string
  /** The cache lookup key, when applicable. */
  key?: string | null
  /** The eviction tag, when applicable. */
  tag?: string | null
  /**
   * The value that caused the failure, when one exists: the snapshot being
   * written (`E_RELATION_CACHE_WRITE_FAILED`) or the cached snapshot that failed
   * to rehydrate (`E_RELATION_CACHE_REHYDRATION_FAILED`). Absent for read /
   * resolution / eviction failures, which have no causing payload.
   */
  value?: unknown
}

/**
 * Renders a failure-causing value into a short, throw-safe string for an exception
 * message. NEVER throws — a `bigint` (Lucid PKs would otherwise trip
 * `JSON.stringify`), a circular reference, or a hostile/throwing proxy (e.g. a
 * poisoned cached snapshot) all degrade to a placeholder. Truncated to keep
 * messages bounded; the full value stays available on `context.value`.
 *
 * @param value - The offending value to describe.
 * @returns A truncated, single-line, throw-safe description.
 */
const describeRelationCacheValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return String(value)
  }
  try {
    const serialized = JSON.stringify(value, (_key, entry) =>
      typeof entry === 'bigint' ? `${entry}n` : entry
    )
    if (serialized === undefined) {
      return Object.prototype.toString.call(value)
    }
    return serialized.length > 200 ? `${serialized.slice(0, 200)}…` : serialized
  } catch {
    return '[unserializable value]'
  }
}

/**
 * Base class for every RUNTIME relation-cache operational failure routed through
 * the `onRelationsCacheError` handler (ADR-009). Subclasses pin a per-site `code`;
 * the original throw is preserved as `cause` and structured context as `context`,
 * so a handler can `instanceof` / switch on `.code` and inspect `.cause`/`.context`.
 *
 * These are NOT thrown to the caller — they are reported to the handler while the
 * load degrades to the DB. Misconfiguration (invalid options / key divergence)
 * stays fail-loud and does NOT extend this.
 *
 * @public
 */
export class E_RELATION_CACHE_OPERATION_FAILED extends Exception {
  static status = 500
  static code = 'E_RELATION_CACHE_OPERATION_FAILED'

  /** Structured context describing the failing operation. */
  readonly context: RelationCacheErrorContext

  constructor(message: string, context: RelationCacheErrorContext, cause: unknown) {
    super(message, { status: 500, cause })
    this.context = context
  }
}

/** A cache read (`get`) failed; the load degrades to an uncached DB load (ADR-009). @public */
export class E_RELATION_CACHE_READ_FAILED extends E_RELATION_CACHE_OPERATION_FAILED {
  static code = 'E_RELATION_CACHE_READ_FAILED'

  constructor(context: RelationCacheErrorContext, cause: unknown) {
    super(
      `Relation cache read failed for "${context.model}.${context.relation}" (key "${context.key}"); falling back to the database.`,
      context,
      cause
    )
  }
}

/** A cache write-back (`set`) failed on a miss; caching is skipped, the fetched row is still returned (ADR-009). @public */
export class E_RELATION_CACHE_WRITE_FAILED extends E_RELATION_CACHE_OPERATION_FAILED {
  static code = 'E_RELATION_CACHE_WRITE_FAILED'

  constructor(context: RelationCacheErrorContext, cause: unknown) {
    super(
      `Relation cache write failed for "${context.model}.${context.relation}" (key "${context.key}"); the row was loaded but not cached. Offending value: ${describeRelationCacheValue(context.value)}.`,
      context,
      cause
    )
  }
}

/** A write-through eviction (`deleteByTag`) failed; the business write still commits, staleness stays TTL-bounded (ADR-009). @public */
export class E_RELATION_CACHE_EVICTION_FAILED extends E_RELATION_CACHE_OPERATION_FAILED {
  static code = 'E_RELATION_CACHE_EVICTION_FAILED'

  constructor(context: RelationCacheErrorContext, cause: unknown) {
    super(
      `Relation cache eviction failed for "${context.model}" (tag "${context.tag}"); the write committed, staleness is TTL-bounded.`,
      context,
      cause
    )
  }
}

/** Resolving the cache service (the host resolver threw, or the app/cache was unreachable) failed; the load degrades to an uncached DB load (ADR-009). @public */
export class E_RELATION_CACHE_RESOLUTION_FAILED extends E_RELATION_CACHE_OPERATION_FAILED {
  static code = 'E_RELATION_CACHE_RESOLUTION_FAILED'

  constructor(context: RelationCacheErrorContext, cause: unknown) {
    super(
      `Relation cache resolution failed for "${context.model}"; falling back to the database.`,
      context,
      cause
    )
  }
}

/** Rehydrating a cached snapshot into a model instance failed (a poisoned/incompatible entry); the load degrades to an uncached DB load that overwrites the entry (ADR-009). @public */
export class E_RELATION_CACHE_REHYDRATION_FAILED extends E_RELATION_CACHE_OPERATION_FAILED {
  static code = 'E_RELATION_CACHE_REHYDRATION_FAILED'

  constructor(context: RelationCacheErrorContext, cause: unknown) {
    super(
      `Relation cache rehydration failed for "${context.model}.${context.relation}" (key "${context.key}"); refetching from the database. Cached value: ${describeRelationCacheValue(context.value)}.`,
      context,
      cause
    )
  }
}

export class E_INVALID_RESOUREFUL_READ_RELATIONSHIP_REQUEST_EXCEPTION extends Exception {
  static status = 422
  static code = 'E_INVALID_RESOUREFUL_READ_RELATIONSHIP_REQUEST_EXCEPTION'

  declare readonly details?: ValidationError['details']

  constructor(reason: E_INVALID_RESOUREFUL_INDEX_REQUEST_EXCEPTION) {
    super(reason.message, {
      code: 'E_INVALID_RESOUREFUL_READ_RELATIONSHIP_REQUEST_EXCEPTION',
      status: 422,
      cause: reason.cause,
    })
    Object.defineProperty(this, 'details', {
      value: reason?.details,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_LUCENE_REGEX_NOT_SUPPORTED extends Exception {
  static status = 501
  static code = 'E_LUCENE_REGEX_NOT_SUPPORTED'

  constructor() {
    const msg: string = `Lucene regex queries are not supported by this database.`
    super(msg, {
      code: 'E_LUCENE_REGEX_NOT_SUPPORTED',
      status: 501,
    })
  }
}

export class E_BULK_UPDATE_SEARCH_UNKNOWN_EXCEPTION extends Exception {
  static status = 500
  static code = 'E_BULK_UPDATE_SEARCH_UNKNOWN_EXCEPTION'

  constructor() {
    const msg: string = `An unknown error occurred during bulk update search operation.`
    super(msg, {
      code: 'E_BULK_UPDATE_SEARCH_UNKNOWN_EXCEPTION',
      status: 500,
    })
  }
}

/**
 * Exception thrown when an invalid field is specified for aggregation operations.
 *
 * This error occurs when attempting to perform aggregations on fields that:
 * - Don't exist in the model
 * - Are not marked as aggregatable in their column definition
 * - Are computed accessors or relationships (which cannot be aggregated at the database level)
 *
 * @example
 * ```typescript
 * // This would throw if 'nonExistentField' doesn't exist or isn't aggregatable
 * await User.$onResourcefulIndex('*', 1, 10, ['id'], null, ctx, app, [], {
 *   nonExistentField: ['sum', 'avg']
 * })
 * ```
 */
export class E_INVALID_AGGREGATION_FIELD extends Exception {
  static status = 422
  static code = 'E_INVALID_AGGREGATION_FIELD'

  constructor(fieldName: string, modelName: string) {
    const msg: string = `The field "${fieldName}" cannot be aggregated in model "${modelName}"`
    super(msg, {
      code: 'E_INVALID_AGGREGATION_FIELD',
      status: 422,
    })
  }
}

/**
 * Exception thrown when an invalid aggregation operation is specified for a field.
 *
 * This error occurs when attempting to perform aggregation operations that are not
 * supported by the library or when the operation name is misspelled. Valid operations
 * include: 'avg', 'min', 'max', 'sum', 'countDistinct', 'sumDistinct', 'avgDistinct'.
 *
 * @example
 * ```typescript
 * // This would throw if 'invalidOperation' is not a valid aggregation method
 * await User.$onResourcefulIndex('*', 1, 10, ['id'], null, ctx, app, [], {
 *   totalSales: ['invalidOperation']
 * })
 * ```
 */
export class E_INVALID_AGGREGATION_OPERATION extends Exception {
  static status = 422
  static code = 'E_INVALID_AGGREGATION_OPERATION'

  constructor(operationName: string, fieldName: string, modelName: string) {
    const msg: string = `The field "${fieldName}" does not support the aggregation operation "${operationName}" in model "${modelName}"`
    super(msg, {
      code: 'E_INVALID_AGGREGATION_OPERATION',
      status: 422,
    })
  }
}

export class E_INVALID_RESOURCEFUL_ROUTER_OPTIONS extends Exception {
  static status = 500
  static code = 'E_INVALID_RESOURCEFUL_ROUTER_OPTIONS'

  declare readonly details?: ValidationError['details']

  constructor(reason?: ValidationError) {
    super(messageFromJoiValidationError(reason, 'Invalid resourceful router options provided'), {
      code: 'E_INVALID_RESOURCEFUL_ROUTER_OPTIONS',
      status: 500,
      cause: reason,
    })
    Object.defineProperty(this, 'details', {
      value: reason?.details,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_UNRESOLVABLE_MODEL extends Exception {
  static status = 500
  static code = 'E_UNRESOLVABLE_MODEL'

  constructor(path: string, reason?: unknown) {
    super(format('The value resolved for path "%s" is not a valid ResourcefulModel', path), {
      code: 'E_UNRESOLVABLE_MODEL',
      status: 500,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class E_INVALID_RESOURCEFUL_MODELS_MAP extends Exception {
  static status = 500
  static code = 'E_INVALID_RESOURCEFUL_MODELS_MAP'

  declare readonly details?: ValidationError['details']

  constructor(reason?: ValidationError) {
    super(messageFromJoiValidationError(reason, 'Invalid models map provided'), {
      code: 'E_INVALID_RESOURCEFUL_MODELS_MAP',
      status: 500,
      cause: reason,
    })
    Object.defineProperty(this, 'details', {
      value: reason?.details,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_INVALID_RESOURCEFUL_MODELS_MAP_AGGREGATE extends Exception {
  static status = 500
  static code = 'E_INVALID_RESOURCEFUL_MODELS_MAP_AGGREGATE'

  declare readonly causes: (E_UNRESOLVABLE_MODEL | E_INVALID_RESOURCEFUL_MODELS_MAP)[]

  constructor(causes: (E_UNRESOLVABLE_MODEL | E_INVALID_RESOURCEFUL_MODELS_MAP)[]) {
    super('Invalid models map provided', {
      code: 'E_INVALID_RESOURCEFUL_MODELS_MAP_AGGREGATE',
      status: 500,
    })
    Object.defineProperty(this, 'causes', {
      value: causes,
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_RESOURCEFUL_ROUTER_MISCONFIGURED extends Exception {
  static status = 500
  static code = 'E_RESOURCEFUL_ROUTER_MISCONFIGURED'

  constructor(reason?: unknown) {
    super('Call to "router.resourceful" is misconfigured', {
      code: 'E_RESOURCEFUL_ROUTER_MISCONFIGURED',
      status: 500,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export const E_ROUTE_NOT_FOUND = createError<[method: string, url: string]>(
  'Cannot %s:%s',
  'E_ROUTE_NOT_FOUND',
  404
)

export class E_NOT_IN_MODEL_CONTEXT extends Exception {
  static status = 500
  static code = 'E_NOT_IN_MODEL_CONTEXT'

  constructor(reason?: unknown) {
    super('Operation is not running within Resourceful Router Model context', {
      code: 'E_NOT_IN_MODEL_CONTEXT',
      status: 500,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export const E_INVALID_HTTP_METHOD_EXCEPTION = createError<[method: string, path: string]>(
  'Invalid HTTP method "%s" for path "%s"',
  'E_INVALID_HTTP_METHOD_EXCEPTION',
  500
)

export {
  UNIQUE_CONSTRAINT_VIOLATION,
  FOREIGN_KEY_VIOLATION,
  NOT_NULL_VIOLATION,
  CHECK_CONSTRAINT_VIOLATION,
  DATA_VALIDATION_ERROR,
  INVALID_DATA_TYPE,
  DEADLOCK,
  LOCK_TIMEOUT,
  SCHEMA_ERROR,
  DATABASE_CONNECTION_ERROR,
  PERMISSION_DENIED,
  SERIALIZATION_FAILURE,
  QUERY_SYNTAX_ERROR,
  DIVISION_BY_ZERO,
} from './private/router_macro/utils/errors'

export class E_NOT_IN_ODATA_CONTEXT extends Exception {
  static status = 500
  static code = 'E_NOT_IN_ODATA_CONTEXT'

  constructor(reason?: unknown) {
    super('Operation is not running within Resourceful OData context', {
      code: 'E_NOT_IN_ODATA_CONTEXT',
      status: 500,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class E_UNACCEPTABLE_FORMAT extends Exception {
  static status = 406
  static code = 'E_UNACCEPTABLE_FORMAT'

  constructor(fmt: string, reason?: unknown) {
    super(`Unable to serve content in the requested "${fmt}" format`, {
      code: 'E_UNACCEPTABLE_FORMAT',
      status: 406,
      cause: reason instanceof Error ? reason : undefined,
    })
  }
}

export class E_INVALID_ENTITYSET_IDENTIFIERS extends Exception {
  static status = 500
  static code = 'E_INVALID_ENTITYSET_IDENTIFIERS'

  declare readonly details: ValidationError['details']
  readonly help: string =
    'https://docs.oasis-open.org/odata/odata-csdl-json/v4.0/odata-csdl-json-v4.0.html#sec_SimpleIdentifier'

  constructor(causes: Set<string>) {
    super(
      `Invalid entity set identifiers provided: ${Array.from(causes)
        .map((name) => `"${name}"`)
        .join(', ')}`,
      {
        code: 'E_INVALID_ENTITYSET_IDENTIFIERS',
        status: 500,
      }
    )
    Object.defineProperty(this, 'details', {
      value: Array.from(causes).map((name) => ({
        message: `The entity set name "${name}" is not a valid OData SimpleIdentifier`,
        path: [name],
        type: 'simpleIdentifier',
      })),
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_EXPAND_NOT_IMPLEMENTED extends Exception {
  static status = 501
  static code = 'E_EXPAND_NOT_IMPLEMENTED'

  declare readonly details: ValidationError['details']

  constructor(modelName: string, causes: Array<string>) {
    super(`$expand is not support for "${modelName}".`, {
      code: 'E_EXPAND_NOT_IMPLEMENTED',
      status: 501,
    })
    Object.defineProperty(this, 'details', {
      value: causes.map(
        (name) =>
          ({
            message: `$expand is not support for "${modelName}.${name}".`,
            path: ['$expand', name],
            type: 'notImplemented',
          }) satisfies ValidationError['details'][number]
      ),
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_UNEXPECTED_CHARACTER extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_UNEXPECTED_CHARACTER'

  declare readonly details: ValidationError['details']

  constructor(char: string, position: number) {
    super(`Unexpected character '${char}' at position ${position}.`, {
      code: 'E_ODATA_FILTER_UNEXPECTED_CHARACTER',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: `Unexpected character '${char}' at position ${position}.`,
          path: ['filter', 'expression', position],
          type: 'unexpectedCharacter',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_UNEXPECTED_TYPE extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_UNEXPECTED_TYPE'

  declare readonly details: ValidationError['details']

  constructor(type: string, token: any, value?: string) {
    super(
      `Expected ${type}${value ? ` with value '${value}'` : ''}, got ${token ? `${token.type} '${token.value}'` : 'end of input'}`,
      {
        code: 'E_ODATA_FILTER_UNEXPECTED_TYPE',
        status: 400,
      }
    )
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: `Expected ${type}${value ? ` with value '${value}'` : ''}, got ${token ? `${token.type} '${token.value}'` : 'end of input'}`,
          path: ['filter', 'expression'],
          type: 'unexpectedCharacter',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_UNEXPECTED_END_OF_INPUT extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_UNEXPECTED_END_OF_INPUT'

  declare readonly details: ValidationError['details']

  constructor() {
    super('Unexpected end of input', {
      code: 'E_ODATA_FILTER_UNEXPECTED_END_OF_INPUT',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: 'Unexpected end of input',
          path: ['filter', 'expression'],
          type: 'unexpectedCharacter',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_UNKNOWN_PARAMETER_ALIAS extends Exception {
  static status = 400
  static code = 'E_ODATA_UNKNOWN_PARAMETER_ALIAS'

  declare readonly details: ValidationError['details']

  constructor(alias: string) {
    super(`Unknown parameter alias: @${alias}`, {
      code: 'E_ODATA_UNKNOWN_PARAMETER_ALIAS',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: `Unknown parameter alias: @${alias}`,
          path: ['filter', 'expression'],
          type: 'unknownParameterAlias',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_UNEXPECTED_TOKEN extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_UNEXPECTED_TOKEN'

  declare readonly details: ValidationError['details']

  constructor(token: any) {
    super(`Unexpected token: ${token.type} '${token.value}'`, {
      code: 'E_ODATA_FILTER_UNEXPECTED_TOKEN',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: `Unexpected token: ${token.type} '${token.value}'`,
          path: ['filter', 'expression'],
          type: 'unexpectedToken',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_INAPPLICABLE_NODE_TYPE extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_INAPPLICABLE_NODE_TYPE'

  declare readonly details: ValidationError['details']

  constructor(node: any) {
    super(`Cannot apply node type '${node.type}' as a filter condition`, {
      code: 'E_ODATA_FILTER_INAPPLICABLE_NODE_TYPE',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: `Cannot apply node type '${node.type}' as a filter condition`,
          path: ['filter', 'expression'],
          type: 'inapplicableNodeType',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_UNKNOWN_LOGICAL_OPERATOR extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_UNKNOWN_LOGICAL_OPERATOR'

  declare readonly details: ValidationError['details']

  constructor(node: any) {
    super(`Unknown logical operator: ${node.operator}`, {
      code: 'E_ODATA_FILTER_UNKNOWN_LOGICAL_OPERATOR',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: `Unknown logical operator: ${node.operator}`,
          path: ['filter', 'expression'],
          type: 'unknownLogicalOperator',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_UNKNOWN_COMPARISON_OPERATOR extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_UNKNOWN_COMPARISON_OPERATOR'

  declare readonly details: ValidationError['details']

  constructor(node: any) {
    super(`Unknown comparison operator: ${node.operator}`, {
      code: 'E_ODATA_FILTER_UNKNOWN_COMPARISON_OPERATOR',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: `Unknown comparison operator: ${node.operator}`,
          path: ['filter', 'expression'],
          type: 'unknownComparisonOperator',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_INVALID_COMPARISON_SIDE extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_INVALID_COMPARISON_SIDE'

  declare readonly details: ValidationError['details']

  constructor() {
    super('Invalid comparison: left side must be a property or function', {
      code: 'E_ODATA_FILTER_INVALID_COMPARISON_SIDE',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: 'Invalid comparison: left side must be a property or function',
          path: ['filter', 'expression'],
          type: 'invalidComparisonSide',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_UNEVALUATABLE_NODE_TYPE extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_UNEVALUATABLE_NODE_TYPE'

  declare readonly details: ValidationError['details']

  constructor(node: any) {
    super(`Cannot evaluate node type: ${node.type}`, {
      code: 'E_ODATA_FILTER_UNEVALUATABLE_NODE_TYPE',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: `Cannot evaluate node type: ${node.type}`,
          path: ['filter', 'expression'],
          type: 'unevaluatableNodeType',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_UNSUPPORTED_FUNCTION_COMPARISON extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_UNSUPPORTED_FUNCTION_COMPARISON'

  declare readonly details: ValidationError['details']

  constructor(funcName: string) {
    super(`Unsupported function comparison: ${funcName}`, {
      code: 'E_ODATA_FILTER_UNSUPPORTED_FUNCTION_COMPARISON',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: `Unsupported function comparison: ${funcName}`,
          path: ['filter', 'expression'],
          type: 'unsupportedFunctionComparison',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_ODATA_FILTER_EXPECTED_COLUMN_REFERENCE extends Exception {
  static status = 400
  static code = 'E_ODATA_FILTER_EXPECTED_COLUMN_REFERENCE'

  declare readonly details: ValidationError['details']

  constructor() {
    super('Expected a column reference', {
      code: 'E_ODATA_FILTER_EXPECTED_COLUMN_REFERENCE',
      status: 400,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: 'Expected a column reference',
          path: ['filter', 'expression'],
          type: 'expectedColumnReference',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export class E_UNSUPPORTED_DATABASE_DIALECT extends Exception {
  static status = 503
  static code = 'E_UNSUPPORTED_DATABASE_DIALECT'

  declare readonly details: ValidationError['details']

  constructor(dialect: string) {
    super(`Unsupported database dialect: ${dialect}`, {
      code: 'E_UNSUPPORTED_DATABASE_DIALECT',
      status: 503,
    })
    Object.defineProperty(this, 'details', {
      value: [
        {
          message: `Unsupported database dialect: ${dialect}`,
          path: ['database', 'dialect'],
          type: 'unsupportedDatabaseDialect',
        },
      ],
      enumerable: true,
      writable: false,
      configurable: false,
    })
  }
}

export const E_ENCODED_BODY_PARSING_FAILED = createError(
  'An unexpected error occurred while parsing the encoded request body',
  'E_ENCODED_BODY_PARSING_FAILED',
  500
)

export const E_MISSING_CURRENT_INSTANCE_ID = createError(
  'When requesting choices for a self-referential relationship, you must provide the current instance ID to avoid including itself in the choices',
  'E_MISSING_CURRENT_INSTANCE_ID',
  422
)
